import { useState } from "react";
import {
  Undo2, Redo2, Bold, Italic, Underline, Strikethrough, Subscript, Superscript, Baseline, Highlighter, RemoveFormatting,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, List, ListOrdered, IndentIncrease, IndentDecrease, ArrowUpDown, MoveHorizontal, AArrowUp, AArrowDown, ChevronDown,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../../ui/dropdown-menu";
import { RB, Group, ColorBtn, NSelect } from "./parts";
import { exec, applyInline, setBlockStyle, indent, setListStyle } from "../cmd";
import { FONTS, SIZES } from "../../../lib/pages";

const Menu = ({ icon: Icon, label, testid, children }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <button type="button" className="rb px-1.5 gap-0.5" title={label} aria-label={label} data-testid={testid} onMouseDown={(e) => e.preventDefault()}>
        <Icon className="h-4 w-4" strokeWidth={1.75} /><ChevronDown className="h-3 w-3 opacity-60" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>{children}</DropdownMenuContent>
  </DropdownMenu>
);

const STYLES = [["p", "Normal"], ["h1", "Heading 1"], ["h2", "Heading 2"], ["h3", "Heading 3"], ["blockquote", "Quote"]];

export const HomeTab = ({ fmt }) => {
  const [color, setColor] = useState("#C00000");
  const [hl, setHl] = useState("#FFF176");
  const step = (d) => {
    const i = SIZES.findIndex((s) => s >= Number(fmt.size || 11));
    const n = SIZES[Math.max(0, Math.min(SIZES.length - 1, (i < 0 ? 4 : i) + d))];
    applyInline("fontSize", `${n}pt`);
  };
  return (
    <>
      <Group label="History">
        <RB icon={Undo2} label="Undo (Ctrl+Z)" onClick={() => exec("undo")} testid="rb-undo" />
        <RB icon={Redo2} label="Redo (Ctrl+Y)" onClick={() => exec("redo")} testid="rb-redo" />
      </Group>
      <Group label="Font">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <NSelect width={148} label="Font family" testid="rb-font-family" value={fmt.font}
              options={[{ value: "", label: "Font…" }, ...FONTS.map((f) => ({ value: f.value, label: f.label }))]}
              onChange={(v) => v && exec("fontName", v)} />
            <NSelect width={62} label="Font size" testid="rb-font-size" value={fmt.size}
              options={[{ value: "", label: "–" }, ...SIZES.map((s) => ({ value: String(s), label: String(s) }))]}
              onChange={(v) => v && applyInline("fontSize", `${v}pt`)} />
            <RB icon={AArrowUp} label="Grow font" onClick={() => step(1)} testid="rb-font-grow" />
            <RB icon={AArrowDown} label="Shrink font" onClick={() => step(-1)} testid="rb-font-shrink" />
          </div>
          <div className="flex items-center gap-0.5">
            <RB icon={Bold} label="Bold (Ctrl+B)" active={fmt.bold} onClick={() => exec("bold")} testid="rb-bold" />
            <RB icon={Italic} label="Italic (Ctrl+I)" active={fmt.italic} onClick={() => exec("italic")} testid="rb-italic" />
            <RB icon={Underline} label="Underline (Ctrl+U)" active={fmt.underline} onClick={() => exec("underline")} testid="rb-underline" />
            <RB icon={Strikethrough} label="Strikethrough" active={fmt.strike} onClick={() => exec("strikeThrough")} testid="rb-strike" />
            <RB icon={Subscript} label="Subscript" onClick={() => exec("subscript")} testid="rb-subscript" />
            <RB icon={Superscript} label="Superscript" onClick={() => exec("superscript")} testid="rb-superscript" />
            <ColorBtn icon={Baseline} label="Font color" value={color} testid="rb-font-color" onChange={(v) => { setColor(v); exec("foreColor", v); }} />
            <ColorBtn icon={Highlighter} label="Text highlight color" value={hl} testid="rb-highlight-color" onChange={(v) => { setHl(v); exec("hiliteColor", v); }} />
            <RB icon={RemoveFormatting} label="Clear formatting" onClick={() => exec("removeFormat")} testid="rb-clear-format" />
          </div>
        </div>
      </Group>
      <Group label="Paragraph">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-0.5">
            <RB icon={List} label="Bullets" active={fmt.ul} onClick={() => exec("insertUnorderedList")} testid="rb-bullets" />
            <Menu icon={List} label="Bullet & numbering styles" testid="rb-list-styles">
              <DropdownMenuLabel className="text-xs">Bullets</DropdownMenuLabel>
              {[["disc", "● Filled circle"], ["circle", "○ Hollow circle"], ["square", "■ Square"]].map(([v, l]) => (
                <DropdownMenuItem key={v} onSelect={() => setListStyle(v, false)} data-testid={`list-style-${v}`}>{l}</DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Numbering</DropdownMenuLabel>
              {[["decimal", "1. 2. 3."], ["lower-alpha", "a. b. c."], ["upper-alpha", "A. B. C."], ["lower-roman", "i. ii. iii."], ["upper-roman", "I. II. III."]].map(([v, l]) => (
                <DropdownMenuItem key={v} onSelect={() => setListStyle(v, true)} data-testid={`list-style-${v}`}>{l}</DropdownMenuItem>
              ))}
            </Menu>
            <RB icon={ListOrdered} label="Numbering" active={fmt.ol} onClick={() => exec("insertOrderedList")} testid="rb-numbering" />
            <RB icon={IndentDecrease} label="Decrease indent" onClick={() => indent(-1)} testid="rb-outdent" />
            <RB icon={IndentIncrease} label="Increase indent" onClick={() => indent(1)} testid="rb-indent" />
          </div>
          <div className="flex items-center gap-0.5">
            <RB icon={AlignLeft} label="Align left" active={fmt.align === "left"} onClick={() => exec("justifyLeft")} testid="rb-align-left" />
            <RB icon={AlignCenter} label="Center" active={fmt.align === "center"} onClick={() => exec("justifyCenter")} testid="rb-align-center" />
            <RB icon={AlignRight} label="Align right" active={fmt.align === "right"} onClick={() => exec("justifyRight")} testid="rb-align-right" />
            <RB icon={AlignJustify} label="Justify" active={fmt.align === "justify"} onClick={() => exec("justifyFull")} testid="rb-align-justify" />
            <Menu icon={ArrowUpDown} label="Line spacing" testid="rb-line-spacing">
              <DropdownMenuLabel className="text-xs">Line spacing</DropdownMenuLabel>
              {["1", "1.15", "1.5", "2", "2.5", "3"].map((v) => (
                <DropdownMenuItem key={v} onSelect={() => setBlockStyle("lineHeight", v)} data-testid={`line-spacing-${v}`}>{Number(v).toFixed(v.length > 3 ? 2 : 1)}</DropdownMenuItem>
              ))}
            </Menu>
            <Menu icon={MoveHorizontal} label="Character spacing" testid="rb-char-spacing">
              <DropdownMenuLabel className="text-xs">Character spacing</DropdownMenuLabel>
              {[["-0.5px", "Condensed"], ["normal", "Normal"], ["1px", "Expanded 1pt"], ["2px", "Expanded 2pt"], ["4px", "Expanded 3pt"]].map(([v, l]) => (
                <DropdownMenuItem key={v} onSelect={() => applyInline("letterSpacing", v)} data-testid={`char-spacing-${l.split(" ")[0].toLowerCase()}${v.replace(/[^0-9]/g, "")}`}>{l}</DropdownMenuItem>
              ))}
            </Menu>
          </div>
        </div>
      </Group>
      <Group label="Styles">
        <div className="grid grid-cols-3 gap-1">
          {STYLES.map(([tag, label]) => (
            <button key={tag} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("formatBlock", tag)} data-testid={`style-${tag}`}
              className={`style-chip ${fmt.block === tag ? "border-primary text-primary" : ""}`}>
              <span className={tag === "h1" ? "font-bold" : tag === "h2" ? "font-semibold" : tag === "blockquote" ? "italic" : ""}>{label}</span>
            </button>
          ))}
        </div>
      </Group>
    </>
  );
};
